import { ErrorMessage, Field } from "formik";
import { IMAGE_FETCH_URL, IMAGE_URL } from "../../config";
import { useState, useRef, useEffect } from "react";
import get from "lodash/get";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FormVideoField = ({
  label = "Upload Video",
  name = "videoField",
  className = "",
  accept = "video/*",
  required = false,
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const uploadFile = (file, form, name) => {
    if (!file) return;

    // Revoke previous blob preview if present
    if (previewUrl) URL.revokeObjectURL(previewUrl);

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onloadstart = () => {
      setIsUploading(true);
      setUploadProgress(0);
    };

    xhr.onload = () => {
      setIsUploading(false);

      if (xhr.status === 200) {
        try {
          const response = JSON.parse(xhr.responseText);
          const filePath = response?.data?.filePath;

          if (!filePath) {
            throw new Error("Invalid server payload");
          }

          const fullUrl = filePath.startsWith("http")
            ? filePath
            : `${IMAGE_FETCH_URL}${filePath}`;

          form.setFieldValue(name, {
            url: fullUrl,
            name: file.name,
          });
          form.setFieldTouched(name, true, false);
        } catch (e) {
          alert("Failed to parse response payload!");
        }
      } else {
        alert("Video upload failed!");
      }
      resetInput();
    };

    xhr.onerror = () => {
      setIsUploading(false);
      alert("An error occurred during video upload!");
      resetInput();
    };

    xhr.send(formData);
  };

  const handleFileSelect = (event, form, name) => {
    const file = event.target.files?.[0];
    uploadFile(file, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer?.files?.[0];
    uploadFile(file, form, name);
  };

  const handleRemoveVideo = (form, name) => {
    form.setFieldValue(name, null);
    form.setFieldTouched(name, true, false);
    resetInput();

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const fileData = get(form.values, name);
        const hasError = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-1.5 w-full ${className}`}>
            {label && (
              <label
                className={`text-fs_14 md:text-fs_16 font-medium capitalize select-none transition-colors ${
                  hasError ? "text-errorColor" : "text-darkText"
                }`}
              >
                {formatUnderscoreToSpace(label)}
                {required && <span className="text-errorColor ml-0.5">*</span>}
              </label>
            )}

            {!fileData ? (
              <div
                className={`border-2 border-dashed rounded-xl p-4 transition-all w-full max-w-[320px] h-[190px] relative overflow-hidden cursor-pointer flex flex-col items-center justify-center bg-gray-50/50 ${
                  isDragActive
                    ? "border-primary bg-primary/5"
                    : hasError
                      ? "border-errorColor bg-red-50/30"
                      : "border-slate-300 hover:border-primary hover:bg-slate-50"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => handleDrop(e, form, name)}
                onClick={() => inputRef.current?.click()}
              >
                {/* Local Preview Video */}
                {previewUrl && (
                  <video
                    src={previewUrl}
                    className="absolute inset-0 w-full h-full object-cover opacity-30"
                    muted
                    playsInline
                  />
                )}

                {/* Upload Overlay */}
                {isUploading ? (
                  <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-xs flex flex-col items-center justify-center text-white z-10 px-6">
                    <p className="text-sm font-semibold mb-2 truncate max-w-full">
                      Uploading video...
                    </p>

                    <div className="w-full bg-white/30 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-400 transition-all duration-150"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>

                    <p className="text-xs font-mono mt-2">{uploadProgress}%</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center z-10 text-center gap-2 p-2">
                    <div className="p-3 bg-white rounded-full shadow-xs border border-slate-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6 text-slate-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
                        />
                      </svg>
                    </div>
                    <p className="text-slate-600 text-xs md:text-sm">
                      Drag & drop video or{" "}
                      <span className="text-primary font-semibold underline underline-offset-2">
                        Browse
                      </span>
                    </p>
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept={accept}
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, form, name)}
                />
              </div>
            ) : (
              <div className="w-full max-w-[320px] bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                <div className="flex items-center justify-between p-2.5 bg-slate-50 border-b border-slate-100">
                  <p className="text-xs font-medium text-slate-700 truncate max-w-[200px]">
                    {fileData.name}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleRemoveVideo(form, name)}
                    className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Remove
                  </button>
                </div>
                <div className="p-2">
                  <video
                    src={fileData.url}
                    controls
                    className="w-full rounded-lg h-[160px] object-cover bg-black"
                  />
                </div>
              </div>
            )}

            <div className="text-errorColor text-fs_14 ps-1 min-h-[1.25rem] flex items-center">
              <ErrorMessage name={name} />
            </div>
          </div>
        );
      }}
    </Field>
  );
};
